import { useTranslation } from 'react-i18next';
import Chart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import { Card, CardTitle } from '@/components/ui';
import { useTheme } from '@/hooks/useTheme';

interface GeoChartProps {
  data: { country: string; clicks: number }[];
  title?: string;
}

const GeoChart = ({ data, title }: GeoChartProps) => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const displayTitle = title ?? t('stats.topCountries');

  const getCountryFlag = (countryCode: string): string => {
    if (!countryCode || countryCode === 'Unknown' || countryCode.length !== 2) {
      return '🌍';
    }
    // Convert country code to flag emoji using regional indicator symbols
    const codePoints = countryCode
      .toUpperCase()
      .split('')
      .map((char) => 0x1f1e6 + char.charCodeAt(0) - 65);
    return String.fromCodePoint(...codePoints);
  };

  if (data.length === 0) {
    return (
      <Card variant="default" padding="lg">
        <CardTitle className="mb-6">{displayTitle}</CardTitle>
        <div className="h-[250px] flex items-center justify-center text-gray-400">
          {t('common.noData')}
        </div>
      </Card>
    );
  }

  const topData = data.slice(0, 5);
  const categories = topData.map(
    (item) => `${getCountryFlag(item.country)} ${item.country}`
  );
  const series = [
    {
      name: t('stats.clicks'),
      data: topData.map((item) => item.clicks),
    },
  ];

  const options: ApexOptions = {
    chart: {
      type: 'bar',
      toolbar: { show: false },
      background: 'transparent',
      fontFamily: 'Inter, system-ui, sans-serif',
    },
    theme: {
      mode: isDark ? 'dark' : 'light',
    },
    plotOptions: {
      bar: {
        horizontal: true,
        borderRadius: 8,
        borderRadiusApplication: 'end',
        barHeight: '60%',
      },
    },
    colors: ['#D4E157'],
    xaxis: {
      categories,
      labels: {
        style: {
          colors: isDark ? '#9CA3AF' : '#6B7280',
          fontSize: '12px',
        },
      },
      axisBorder: {
        color: isDark ? '#374151' : '#E5E7EB',
      },
      axisTicks: { show: false },
    },
    yaxis: {
      labels: {
        style: {
          colors: isDark ? '#F9FAFB' : '#1A1A1A',
          fontSize: '12px',
        },
      },
    },
    grid: {
      borderColor: isDark ? '#374151' : '#f0f0f0',
      strokeDashArray: 3,
      xaxis: {
        lines: { show: false },
      },
    },
    tooltip: {
      theme: isDark ? 'dark' : 'light',
      y: {
        formatter: (value: number) => `${value} ${t('stats.clicks').toLowerCase()}`,
      },
    },
    dataLabels: {
      enabled: false,
    },
  };

  return (
    <Card variant="default" padding="lg">
      <CardTitle className="mb-6">{displayTitle}</CardTitle>
      <div className="h-[250px]">
        <Chart
          options={options}
          series={series}
          type="bar"
          height="100%"
          width="100%"
        />
      </div>
    </Card>
  );
};

export default GeoChart;
